import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, AlertCircle, Mail } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { API_BASE_URL } from "@/lib/consts";
import { HelmCompass } from "@/components/helm-logo";

type VerifyState =
	| { status: "loading" }
	| { status: "success" }
	| { status: "error"; message: string };

export const Route = createFileRoute("/auth/verify-email")({
	component: VerifyEmailPage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1).optional() }).parse(search),
});

function VerifyEmailPage() {
	const { token } = Route.useSearch();
	const [state, setState] = useState<VerifyState>({ status: "loading" });
	const [resending, setResending] = useState(false);
	const [resendMessage, setResendMessage] = useState<string | null>(null);

	const verifyToken = useCallback(async (verificationToken: string) => {
		try {
			const res = await fetch(`${API_BASE_URL}/auth/verify-email`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token: verificationToken }),
			});

			if (res.ok) {
				setState({ status: "success" });
			} else {
				const body = await res.json().catch(() => null);
				const message =
					body?.detail || body?.message || "Verification failed. The link may be invalid or expired.";
				setState({ status: "error", message });
			}
		} catch {
			setState({
				status: "error",
				message: "An unexpected error occurred. Please try again.",
			});
		}
	}, []);

	useEffect(() => {
		if (token) {
			verifyToken(token);
		} else {
			setState({
				status: "error",
				message: "No verification token provided.",
			});
		}
	}, [token, verifyToken]);

	const handleResend = async () => {
		setResending(true);
		setResendMessage(null);
		try {
			const tempToken = localStorage.getItem("verification_token");
			if (!tempToken) {
				setResendMessage("No verification session found. Please log in again.");
				return;
			}

			const res = await fetch(`${API_BASE_URL}/auth/resend-verification-email`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ temp_token: tempToken }),
			});

			if (res.ok) {
				setResendMessage("Verification email sent! Check your inbox.");
			} else {
				const body = await res.json().catch(() => null);
				setResendMessage(
					body?.detail || "Failed to resend verification email.",
				);
			}
		} catch {
			setResendMessage("An unexpected error occurred.");
		} finally {
			setResending(false);
		}
	};

	return (
		<div className="flex min-h-svh items-center justify-center bg-muted/40 p-6">
			<div className="mx-auto w-full max-w-md space-y-8">
				<div className="flex justify-center">
					<HelmCompass size={32} color="hsl(22 82% 31%)" strokeWidth={1.2} />
				</div>

				{state.status === "loading" && (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<Spinner className="size-8" />
						<p className="text-muted-foreground">Verifying your email...</p>
					</div>
				)}

				{state.status === "success" && (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
							<CheckCircle2 className="h-8 w-8 text-green-600" />
						</div>
						<h1 className="text-2xl font-semibold">Email Verified!</h1>
						<p className="text-muted-foreground">
							Your email address has been verified successfully.
						</p>
						<Button asChild className="mt-2 w-full">
							<Link to="/login">Go to Login</Link>
						</Button>
					</div>
				)}

				{state.status === "error" && (
					<div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
							<AlertCircle className="h-8 w-8 text-red-600" />
						</div>
						<h1 className="text-2xl font-semibold">Verification Failed</h1>
						<p className="text-muted-foreground">{state.message}</p>
						<div className="mt-2 flex w-full flex-col gap-2">
							<Button
								variant="outline"
								onClick={handleResend}
								disabled={resending}
								className="w-full"
							>
								{resending ? (
									<>
										<Spinner className="mr-2 size-4" />
										Sending...
									</>
								) : (
									<>
										<Mail className="mr-2 h-4 w-4" />
										Resend Verification Email
									</>
								)}
							</Button>
							{resendMessage && (
								<p className="text-sm text-muted-foreground">{resendMessage}</p>
							)}
							<Button asChild variant="ghost" className="w-full">
								<Link to="/login">Back to Login</Link>
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
